import Cookies from "js-cookie";
import { toNumber } from "lodash";
import { useMemo } from "react";
import {
  useAuthorizedPersonQuery,
  useViewerLazyQuery,
  useViewerQuery,
  Viewer,
  Person,
  ViewerQuery,
} from "@/features/auth/graphql/generated";

interface ViewerWithPerson extends Viewer {
  person: Person;
}

export const useLazyViewer = () => {
  const [fetchViewer, { data, loading }] = useViewerLazyQuery();

  return {
    data,
    loading,
    fetchViewer,
  };
};

// This hook is used to update the viewer data in the store and update the user in Intercom, Hubspot, and Heap
export const useViewer = () => {
  const authorizedPersonId = toNumber(Cookies.get("authorized_person"));
  const { data: authorizedPersonData } = useAuthorizedPersonQuery({
    skip: !authorizedPersonId,
    fetchPolicy: "cache-first",
  });

  const authorizedPerson = useMemo(() => {
    return authorizedPersonData?.authorizedPerson || ({} as Person);
  }, [authorizedPersonData?.authorizedPerson]);

  const onViewerFetchComplete = async (data: ViewerQuery) => {
    const {
      id,
      type,
      name,
      email,
      // surveys,
      default_organization_id,
      occupation,
    } = data.viewer || {};

    if (!data.viewer) return;

    if (type === "user") {
      // Intercom updates will be handled by the useIntercom hook when needed
      // This ensures we don't have circular dependencies
    }

    if (window._hsq) {
      window._hsq.push([
        "identify",
        {
          email,
        },
      ]);
    }

    if (window.heap) {
      window.heap.identify(`user-${id}`);
      window.heap.addUserProperties({ name, occupation, email });
    }

    if (default_organization_id && !window.localStorage.recentOrg) {
      window.localStorage.setItem(
        "recentOrg",
        default_organization_id.toString()
      );
    }
  };

  const {
    data,
    loading,
    refetch: fetchViewer,
  } = useViewerQuery({
    skip: !authorizedPersonId,
    onCompleted: (data) => onViewerFetchComplete(data),
    fetchPolicy: "cache-first",
    nextFetchPolicy: "cache-first",
  });

  const viewer = useMemo(() => {
    if (!data?.viewer) return {} as ViewerWithPerson;

    const { viewer } = data;

    return {
      ...viewer,
      person: {
        ...authorizedPerson,
        id: authorizedPersonId,
      },
    } as unknown as ViewerWithPerson;
  }, [data, authorizedPerson]);

  return {
    viewer,
    authorizedPersonId,
    fetchViewer,
  };
};
